import React, { useState, useEffect, useRef } from 'react';
import { Button, Popper, Paper, Box, Typography, ClickAwayListener } from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import GetAppIcon from '@material-ui/icons/GetApp';
import { toast } from 'react-toastify';
import useStyles from './style';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import Dropdown from './Dropdown';
import SimpleTableViewer from '../SimpleTable';
import OverviewCardViewer from '../OverviewCard';
import SpendCardViewer from './SpendCard';
import HeadingBarViewer from '../HeadingBar';
import ExternalFilter from './ExternalFilter';
import LinkSpendModal from './LinkSpendModal';
import AddSpendModal from './AddSpendModal';
import { FormService } from '../../../modules/api/form';

const SpendViewer = (props) => {
    const classes = useStyles();
    const { spendDetails } = props;
    const [filterData, setFilterData] = useState({
    });
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [sort, setSort] = useState({});
    const [filterButtonText, setFilterButtonText] = useState('Filter by');
    const [savedFilterData, setSavedFilterData] = useState(null);

    const [tableData, setTableData] = useState({
    });
    const [isAddSpendModalOpen, setIsAddSpendModalOpen] = useState(false);
    const [addSpendFormData, setAddSpendFormData] = useState({});
    const [isAddSpendSaving, setIsAddSpendSaving] = useState(false);
    const [isLinkSpendModalOpen, setIsLinkSpendModalOpen] = useState(false);
    const [linkModalTableData, setLinkModalTableData] = useState({ data: [] });
    const [linkModalSelectedIds, setLinkModalSelectedIds] = useState([]);
    const [isLinkModalSaving, setIsLinkModalSaving] = useState(false);
    const [isUnlinkSaving, setIsUnlinkSaving] = useState(false);
    const [linkModalPage, setLinkModalPage] = useState(0);
    const [linkModalRowsPerPage, setLinkModalRowsPerPage] = useState(10);
    const [linkModalConditions, setLinkModalConditions] = useState([]);
    const [linkModalFilterData, setLinkModalFilterData] = useState({});

    const linkModalDataUniqueId = `${props.data?.chartUniqueId}_spendDetails_linkModalData`;
    const linkModalFilterDataUniqueId = `${props.data?.chartUniqueId}_spendDetails_linkModalFilterData`;
    const unlinkWhereKey = spendDetails?.spendInformationHeader?.unLinkButton?.where || 'id_spendByContract';
    const linkWhereKey = spendDetails?.spendInformationHeader?.updateButton?.where || 'id_spendByContract';
    const isCheckboxEnabled = Boolean(
        spendDetails?.spendInformation?.checkbox
        || spendDetails?.spendInformationHeader?.unLinkButton?.enable
    );

    /** Resolves template values like `${blockState.documentId}` from block/route state. */
    const resolveTemplateValue = (templateValue) => {
        console.log('resolveTemplateValue**templateValue', templateValue, props.blockState)
        if (templateValue == null) {
            return props.blockState?.documentId || props.match?.params?.id;
        }
        if (typeof templateValue !== 'string') {
            return templateValue;
        }
        const trimmed = templateValue.trim();
        if (trimmed === '${blockState.documentId}' || trimmed === 'blockState.documentId') {
            return props.blockState?.documentId || props.match?.params?.id;
        }
        return templateValue.replace(/\$\{blockState\.documentId\}/g, props.blockState?.documentId || props.match?.params?.id || '');
    };

    /** Refetches the main spend table for the current page/filters. */
    const refetchSpendTable = (overrides = {}) => {
        const currentConditions = overrides.conditions != null ? overrides.conditions : getConditions();
        const nextPage = overrides.page != null ? overrides.page : page;
        const nextRowsPerPage = overrides.rowsPerPage != null ? overrides.rowsPerPage : rowsPerPage;
        const nextSort = overrides.sort != null ? overrides.sort : sort;

        props.getSpendDetails({
            appId: props.match.params.appid,
            dataModelId: spendDetails.dataModelId,
            mainDataModelId: spendDetails.mainDataModelId,
            ...(spendDetails?.linkColumn && { linkColumn: spendDetails?.linkColumn || 'documentId' }),
            documentId: props.match.params.id,
            dataUniqueId: props.data?.chartUniqueId + '_spendDetails_tableData',
            conditions: currentConditions,
            ...(spendDetails?.field && { field: spendDetails?.field || 'documentId' }),
            ...(spendDetails?.targetField && { targetField: spendDetails?.targetField || 'documentId' }),
            ...(spendDetails?.sumField && { sumField: spendDetails?.sumField }),
            ...(spendDetails?.combineFieldsIntoSingleField && { combineFieldsIntoSingleField: true }),
            sort: nextSort,
            payload: {
                dataModelId: spendDetails.dataModelId,
                mainDataModelId: spendDetails.mainDataModelId,
                documentId: props.match.params.id,
                ...(spendDetails?.linkColumn && { linkColumn: spendDetails?.linkColumn || 'documentId' }),
                conditions: currentConditions,
                ...(spendDetails?.targetField && { targetField: spendDetails?.targetField || 'documentId' }),
                ...(spendDetails?.field && { field: spendDetails?.field || 'documentId' }),
                ...(spendDetails?.sumField && { sumField: spendDetails?.sumField }),
                ...(spendDetails?.combineFieldsIntoSingleField && { combineFieldsIntoSingleField: true }),
                sort: nextSort,
                page: nextPage,
                rowsPerPage: nextRowsPerPage
            }
        });
    };

    /** Loads spend candidates into the Link New Spend modal. */
    const fetchLinkModalSpendDetails = ({
        requestedPage = linkModalPage,
        requestedRowsPerPage = linkModalRowsPerPage,
        requestedConditions = linkModalConditions,
    } = {}) => {
        props.getSpendDetails({
            appId: props.match.params.appid,
            dataModelId: spendDetails.dataModelId,
            mainDataModelId: spendDetails.mainDataModelId,
            documentId: props.match.params.id,
            dataUniqueId: linkModalDataUniqueId,
            conditions: requestedConditions,
            sort: sort,
            payload: {
                dataModelId: spendDetails.dataModelId,
                mainDataModelId: spendDetails.mainDataModelId,
                documentId: props.match.params.id,
                conditions: requestedConditions,
                sort: sort,
                ...(spendDetails?.targetField && { targetField: spendDetails?.targetField || 'documentId' }),
                ...(spendDetails?.field && { field: spendDetails?.field || 'documentId' }),
                ...(spendDetails?.sumField && { sumField: spendDetails?.sumField }),
                ...(spendDetails?.combineFieldsIntoSingleField && { combineFieldsIntoSingleField: true }),
                page: requestedPage,
                rowsPerPage: requestedRowsPerPage
            }
        });
    };

    useEffect(() => {
        // The initial spend-table fetch is deferred to the [selectedFilters, isInitialFetchReady]
        // effect below, so it fires exactly once — with a prefilled condition if one resolves from
        // blockState, or without one otherwise — instead of firing here unconditionally too.
        props.getSpendFilters({
            appId: props.match.params.appid,
            dataModelId: spendDetails.dataModelId,
            dataUniqueId: props.data?.chartUniqueId + '_spendDetails_filterData',
            payload: {
                field: spendDetails?.spendInformation?.filter?.map(item => item.value).join(","),
                obligationId: props.match.params.id,
                documentIdField: spendDetails?.linkColumn || 'documentId',
                ...(spendDetails?.linkColumn && { linkColumn: spendDetails?.linkColumn || 'documentId' }),
                ...(spendDetails?.targetField && { targetField: spendDetails?.targetField || 'documentId' }),
                ...(spendDetails?.documentIdField && { documentIdField: spendDetails?.documentIdField || 'documentId' }),
                mainDataModelId: spendDetails.mainDataModelId
            }
        });
        updateFilterCalculations();
    }, [props.activeTabMain]);

    useEffect(() => {
        if (props.formData?.document[props.data?.chartUniqueId + '_spendDetails_tableData']) {
            setTableData(props.formData?.document[props.data?.chartUniqueId + '_spendDetails_tableData'] || {});
            Object.keys(props.formData?.document[props.data?.chartUniqueId + '_spendDetails_tableData']).forEach(key => {
                let dataElement = spendDetails?.uploadCard?.dataElement.find(item => item.value === key);
                console.log('SpendViewer**dataElement', dataElement)
                if (dataElement) {
                    setCardData(prev => ({
                        ...prev,
                        [key]: props.formData?.document[props.data?.chartUniqueId + '_spendDetails_tableData']?.[dataElement.value] || ''
                    }));
                }
            });
        }
    }, [props.formData?.document[props.data?.chartUniqueId + '_spendDetails_tableData']])

    useEffect(() => {
        if (props.formData?.document?.[linkModalDataUniqueId]) {
            setLinkModalTableData(props.formData.document[linkModalDataUniqueId] || { data: [] });
        }
    }, [props.formData?.document?.[linkModalDataUniqueId]]);

    useEffect(() => {
        if (props.formData?.document?.[linkModalFilterDataUniqueId]) {
            setLinkModalFilterData(props.formData.document[linkModalFilterDataUniqueId] || {});
        }
    }, [props.formData?.document?.[linkModalFilterDataUniqueId]]);

    // Applies filter options + blockState prefill + isInitialFetchReady in one synchronous effect.
    // These three used to live in separate effects, which let `isInitialFetchReady` flip true one
    // render before the prefilled `selectedFilters` landed — causing the initial table fetch to
    // fire twice: once without conditions, then again once the prefill committed. Doing all three
    // in a single effect call keeps them in the same commit, so the fetch effect below only runs once.
    useEffect(() => {
        const latestFilterData = props.formData?.document[props.data?.chartUniqueId + '_spendDetails_filterData'];
        if (!latestFilterData) {
            return;
        }
        setFilterData(latestFilterData || {});

        if (!hasPrefilledFilters.current && props.blockState) {
            const prefilledFilters = {};
            const filterKeys = Array.isArray(spendDetails?.spendInformation?.prefillFilters)
                ? spendDetails.spendInformation.prefillFilters
                : [];
            let hasPrefilledValues = false;

            filterKeys.forEach(filterKey => {
                const blockStateValue = props.blockState[filterKey];
                if (blockStateValue !== undefined && blockStateValue !== null && blockStateValue !== '') {
                    // Get available options for this filter
                    const availableOptions = latestFilterData[filterKey] || [];

                    // Convert blockState value to array format expected by ExternalFilter
                    // Handle both single values and arrays
                    const valuesToSet = Array.isArray(blockStateValue) ? blockStateValue : [blockStateValue];

                    // Map values to the format expected by ExternalFilter { label, value }
                    const mappedValues = valuesToSet.map(value => {
                        // Try to find matching option in filterData
                        const matchingOption = availableOptions.find(option => {
                            const optionValue = typeof option === 'object' ? option.value : option;
                            const optionLabel = typeof option === 'object' ? option.label : option;
                            return optionValue === value || optionLabel === value || String(optionValue) === String(value);
                        });

                        if (matchingOption) {
                            // Use the format from filterData
                            return typeof matchingOption === 'object'
                                ? { label: matchingOption.label, value: matchingOption.value }
                                : { label: matchingOption, value: matchingOption };
                        } else {
                            // If no match found, create object from the value
                            const isNumeric = typeof value === 'number' || (value != null && value !== '' && !isNaN(Number(value)));
                            const numVal = typeof value === 'number' ? value : Number(value);
                            return {
                                label: isNumeric ? numVal : (value ?? ''),
                                value: isNumeric ? numVal : String(value ?? '')
                            };
                        }
                    });

                    if (mappedValues.length > 0) {
                        prefilledFilters[filterKey] = mappedValues;
                        hasPrefilledValues = true;
                    }
                }
            });

            // Only set if we have prefilled values
            if (hasPrefilledValues) {
                hasPrefilledFilters.current = true;
                setLocalSelectedFilterData(prefilledFilters);
                setSavedFilterData(prefilledFilters);

                // Convert to conditions format and set selectedFilters
                const conditions = convertFilterValuesToConditions(prefilledFilters);
                setSelectedFilters(conditions);
            }
        }

        // Filter options (and any prefill) are now applied, so it's safe to fire the one
        // initial table-data fetch, in this same commit rather than a follow-up one.
        setIsInitialFetchReady(true);
    }, [props.formData?.document[props.data?.chartUniqueId + '_spendDetails_filterData'], props.blockState])

    const [cardData, setCardData] = useState({});
    const [selectedDocumentIds, setSelectedDocumentIds] = useState([]);

    console.log('SpendViewer**props', props, cardData)


    // State to track selected conditions for each filter
    const [selectedFilters, setSelectedFilters] = useState({});
    // State to control filter popper
    const [anchorEl, setAnchorEl] = useState(null);
    // Temporary state for filters in popper (before saving)
    const [localSelectedFilterData, setLocalSelectedFilterData] = useState(null);
    // Ref to track if filters have been prefilled from blockState
    const hasPrefilledFilters = useRef(false);
    // Tracks the condition set that was last actually fetched, so the effect below never
    // dispatches the same conditions (with or without a condition) twice in a row.
    const lastFetchedConditionsSignatureRef = useRef(null);
    // Gates the initial spend-table fetch so it fires exactly once: immediately if there's no
    // prefill config to wait for, otherwise after the blockState-prefill attempt resolves.
    const [isInitialFetchReady, setIsInitialFetchReady] = useState(() => !(
        Array.isArray(spendDetails?.spendInformation?.prefillFilters)
        && spendDetails.spendInformation.prefillFilters.length > 0
    ));

    // Handle selection change for a specific filter
    const handleSelectionChange = (condition) => {
        if (condition && condition.selectedConditionDataElement) {
            const filterKey = condition.selectedConditionDataElement;
            setSelectedFilters(prev => ({
                ...prev,
                [filterKey]: condition
            }));
            // Log or use the condition object as needed
            console.log('Condition:', condition);
        }
    };

    // Handle clear all for a specific filter
    const handleClearAll = (filterKey) => {
        setSelectedFilters(prev => ({
            ...prev,
            [filterKey]: null
        }));
    };

    // Handle opening filter popper
    const handleFilterClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    // Handle closing filter popper
    const handleFilterClose = (action) => {
        if (action === 'save') {
            // Convert ExternalFilter format to conditions format
            const conditions = convertFilterValuesToConditions(localSelectedFilterData || {});
            setSavedFilterData(localSelectedFilterData);
            setSelectedFilters(conditions);
            updateFilterCalculations();
        } else if (action === 'clear') {
            setLocalSelectedFilterData(null);
            setSavedFilterData(null);
            setSelectedFilters({});
            updateFilterCalculations();
        } else if (action === 'close') {
            const filters = savedFilterData ? savedFilterData : null;

            // Reset local filter data when closing without saving
            setLocalSelectedFilterData(filters);
        }
        setAnchorEl(null);
    };

    // Convert ExternalFilter values to conditions format
    const convertFilterValuesToConditions = (filterValues) => {
        const conditions = {};
        spendDetails?.spendInformation?.filter?.forEach((item) => {
            const filterValue = filterValues[item.value];
            if (filterValue && filterValue.length > 0) {
                conditions[item.value] = {
                    selectedConditionDataElement: item.value,
                    selectedConditionOperator: "in",  // equals
                    value: filterValue.map(v => v.value || v),
                    selectedConditionClause: "and"
                };
            } else {
                conditions[item.value] = null;
            }
        });
        return conditions;
    };

    // Handle external filter change
    const handleExternalFilterChange = (updatedFilters) => {
        setLocalSelectedFilterData(updatedFilters);
    };

    // Check if there are any filter values selected
    const hasLocalFilterValues = () => {
        if (!localSelectedFilterData) return false;
        return Object.values(localSelectedFilterData).some(filter =>
            filter && Array.isArray(filter) && filter.length > 0
        );
    };

    // Get all conditions in the required format
    const getConditions = () => {
        return Object.values(selectedFilters)
            .filter(condition => condition && condition.value && condition.value.length > 0);
    };

    const calculateSelectedFiltersCount = () => {
        const filterValues = localSelectedFilterData !== null ? localSelectedFilterData : {}

        if (!filterValues) return 0;

        let totalCount = 0;
        Object.values(filterValues).forEach((value) => {
            if (value && Array.isArray(value) && value.length > 0) {
                // String/namepicker filters - count each selected item
                totalCount += value.length;
            } else if (
                value &&
                typeof value === 'object' &&
                (value.startDate || value.endDate)
            ) {
                // Date range filter - count as 1 if any date is selected
                if (value.startDate || value.endDate) {
                    totalCount += 1;
                }
            } else if (
                value &&
                Array.isArray(value) &&
                value.length === 2 &&
                typeof value[0] === 'number' &&
                typeof value[1] === 'number'
            ) {
                // Number range filter - count as 1 if range is set
                totalCount += 1;
            }
        });
        return totalCount;
    };

    const updateFilterCalculations = () => {
        const selectedFiltersCount = calculateSelectedFiltersCount();
        const filterButtonText = selectedFiltersCount > 0 ? `Filter added (${selectedFiltersCount})` : 'Filter by';
        setFilterButtonText(filterButtonText);
    };

    // Fetches the spend table whenever filters change — also covers the one initial fetch, once
    // isInitialFetchReady flips true (immediately if unprefilled, or after prefill resolves).
    // Conditions are included only when present; the signature check guards against firing twice
    // for the same resolved condition set (e.g. a duplicate render replaying this effect).
    useEffect(() => {
        if (!isInitialFetchReady) {
            return;
        }
        const conditions = getConditions();
        const signature = JSON.stringify(conditions);
        if (lastFetchedConditionsSignatureRef.current === signature) {
            return;
        }
        lastFetchedConditionsSignatureRef.current = signature;

        updateFilterCalculations();
        refetchSpendTable({ conditions });
        if (conditions.length > 0) {
            console.log('All Conditions:', conditions);
        }
    }, [selectedFilters, isInitialFetchReady]);

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
        const currentConditions = getConditions();
        props.getSpendDetails({
            appId: props.match.params.appid,
            dataModelId: spendDetails.dataModelId,
            mainDataModelId: spendDetails.mainDataModelId,
            documentId: props.match.params.id,
            dataUniqueId: props.data?.chartUniqueId + '_spendDetails_tableData',
            conditions: currentConditions,
            targetField: spendDetails?.targetField || 'documentId',
            field: spendDetails?.field || 'documentId',
            sort: sort,
            payload: {
                dataModelId: spendDetails.dataModelId,
                mainDataModelId: spendDetails.mainDataModelId,
                documentId: props.match.params.id,
                conditions: currentConditions,
                // targetField: spendDetails?.targetField || 'documentId',
                // field: spendDetails?.field || 'documentId',
                sort: sort,
                page: newPage,
                ...(spendDetails?.targetField && { targetField: spendDetails?.targetField || 'documentId' }),
                ...(spendDetails?.field && { field: spendDetails?.field || 'documentId' }),
                ...(spendDetails?.sumField && { sumField: spendDetails?.sumField }),
                ...(spendDetails?.combineFieldsIntoSingleField && { combineFieldsIntoSingleField: true }),
                rowsPerPage: rowsPerPage
            }
        });
    };

    const handleRowsPerPageChange = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0); // Reset to first page when rows per page changes
        const currentConditions = getConditions();
        props.getSpendDetails({
            appId: props.match.params.appid,
            dataModelId: spendDetails.dataModelId,
            mainDataModelId: spendDetails.mainDataModelId,
            documentId: props.match.params.id,
            dataUniqueId: props.data?.chartUniqueId + '_spendDetails_tableData',
            conditions: currentConditions,
            targetField: spendDetails?.targetField || 'documentId',
            field: spendDetails?.field || 'documentId',
            sort: sort,
            payload: {
                dataModelId: spendDetails.dataModelId,
                mainDataModelId: spendDetails.mainDataModelId,
                documentId: props.match.params.id,
                conditions: currentConditions,
                // targetField: spendDetails?.targetField || 'documentId',
                // field: spendDetails?.field || 'documentId',
                sort: sort,
                ...(spendDetails?.targetField && { targetField: spendDetails?.targetField || 'documentId' }),
                ...(spendDetails?.field && { field: spendDetails?.field || 'documentId' }),
                ...(spendDetails?.sumField && { sumField: spendDetails?.sumField }),
                ...(spendDetails?.combineFieldsIntoSingleField && { combineFieldsIntoSingleField: true }),
                page: 0, // Reset to first page
                rowsPerPage: newRowsPerPage
            }
        });
    };

    /** Reads a field from a spend row; unwraps `{ value }` when present. */
    const getSpendFieldValue = (row, fieldKey) => {
        if (!row || !fieldKey) return null;
        const rawValue = row[fieldKey];
        if (rawValue == null || rawValue === '') return null;
        if (typeof rawValue === 'object' && rawValue !== null && 'value' in rawValue) {
            const nestedValue = rawValue.value;
            if (nestedValue == null || nestedValue === '') return null;
            return nestedValue;
        }
        return rawValue;
    };

    /**
     * Maps checkbox selection (unlinkWhereKey) to fetchKey values only for SQL download ids.
     */
    const resolveFetchKeyIdsForSelection = (selectedIds) => {
        const fetchKey = spendDetails?.fetchKey || spendDetails?.actions?.fetchKey;
        if (!fetchKey || !selectedIds?.length) return [];

        const rows = tableData?.data || [];
        return selectedIds
            .map((selectedId) => {
                const matchedRow = rows.find((row) => {
                    const whereKeyId = getSpendFieldValue(row, unlinkWhereKey);
                    const fallbackId = row?.id ?? row?._id;
                    return (
                        (whereKeyId != null && String(whereKeyId) === String(selectedId))
                        || (fallbackId != null && String(fallbackId) === String(selectedId))
                    );
                });
                return matchedRow ? getSpendFieldValue(matchedRow, fetchKey) : null;
            })
            .filter((id) => id != null && id !== '');
    };

    /** Downloads selected spend rows; uses spendLine/download when isSQLEnable is set. */
    const handleDownload = async () => {
        if (selectedDocumentIds.length === 0) {
            console.warn('No documents selected');
            return;
        }

        const fields = spendDetails?.spendInformation?.dataElement?.map(i => i.value).join(',') || '';
        const conditions = getConditions();
        const fetchKey = spendDetails?.fetchKey || spendDetails?.actions?.fetchKey;

        if (spendDetails?.isSQLEnable) {
            try {
                const fetchKeyIds = resolveFetchKeyIdsForSelection(selectedDocumentIds);
                if (fetchKeyIds.length === 0) {
                    console.warn(`Unable to download: selected rows are missing "${fetchKey || 'fetchKey'}" values.`);
                    return;
                }
                await FormService.downloadSpendDataModel({
                    appId: props.match.params.appid,
                    dataModelId: spendDetails.dataModelId,
                    payload: {
                        downloadType: 'excel',
                        fields,
                        ids: fetchKeyIds,
                        fetchKey,
                        conditions,
                    },
                });
                setSelectedDocumentIds([]);
            } catch (error) {
                console.error('handleDownload SQL spend download failed', error);
            }
            return;
        }

        const isDownloadV2Call = checkDownloadV2Call(spendDetails?.spendInformation, tableData?.totalRecords);
        props.downloadSheet({
            dataModelId: spendDetails.dataModelId,
            appid: props.match.params.appid,
            fields,
            downloadType: 'excel',
            conditions,
            ids: selectedDocumentIds,
            dataUniqueId: props.data.chartUniqueId + '_' + 'selected_spend_download',
            isVisible: true,
            key: 'xlsx',
            name: 'Excel',
            _id: '',
            isDownloadV2Call: isDownloadV2Call,
        });
        setSelectedDocumentIds([]);
    };

    /** Downloads a single spend row; uses spendLine/download when isSQLEnable is set. */
    const handleSingleRowDownload = async (e, rowData) => {
        const fields = spendDetails?.spendInformation?.dataElement?.map(i => i.value).join(',') || '';
        const conditions = getConditions();
        const fetchKey = spendDetails?.fetchKey || spendDetails?.actions?.fetchKey;

        if (spendDetails?.isSQLEnable) {
            if (!fetchKey) {
                console.warn('Unable to download: fetchKey is not configured.');
                return;
            }
            const fetchKeyValue = getSpendFieldValue(rowData, fetchKey);
            if (fetchKeyValue == null) {
                console.warn(`Unable to download: selected row is missing "${fetchKey}".`);
                return;
            }
            try {
                await FormService.downloadSpendDataModel({
                    appId: props.match.params.appid,
                    dataModelId: spendDetails.dataModelId,
                    payload: {
                        downloadType: 'excel',
                        fields,
                        ids: [fetchKeyValue],
                        fetchKey,
                        conditions,
                    },
                });
            } catch (error) {
                console.error('handleSingleRowDownload SQL spend download failed', error);
            }
            return;
        }

        const rowId = getSpendFieldValue(rowData, unlinkWhereKey) || rowData?.id || rowData?._id;
        if (!rowId) {
            console.warn('No document ID found in row data');
            return;
        }

        props.downloadSheet({
            dataModelId: spendDetails.dataModelId,
            appid: props.match.params.appid,
            fields,
            downloadType: 'excel',
            conditions,
            ids: [rowId],
            dataUniqueId: props.data.chartUniqueId + '_' + 'single_row' + '_download',
            isVisible: true,
            key: 'xlsx',
            name: 'Excel',
            _id: '',
        });
    };

    const DEFAULT_EXPECTED_RULE_SIZE = 500;
    const checkDownloadV2Call = (spendInformation, totalRecords) => {
        return spendInformation.download?.v2 && (totalRecords >= (spendInformation.download?.expectedRuleSize || DEFAULT_EXPECTED_RULE_SIZE));
    };

    /** Downloads all filtered spend rows; uses spendLine/bulkDownload when isSQLEnable is set. */
    const handleDownloadAll = async () => {
        const currentConditions = getConditions();
        const fields = spendDetails?.spendInformation?.dataElement?.map(i => i.value).join(',') || '';
        const linkField = spendDetails?.targetField || spendDetails?.field || 'documentId';
        const documentId = props.blockState?.documentId || props.match?.params?.id;
        const conditions = [
            {
                selectedConditionClause: 'and',
                selectedConditionDataElement: linkField,
                selectedConditionOperator: 'equals',
                value: documentId,
            },
            ...currentConditions,
        ];

        if (spendDetails?.isSQLEnable) {
            try {
                await FormService.bulkDownloadSpendDataModel({
                    appId: props.match.params.appid,
                    dataModelId: spendDetails.dataModelId,
                    payload: {
                        downloadType: 'excel',
                        fields,
                        conditions,
                    },
                });
            } catch (error) {
                console.error('handleDownloadAll SQL spend bulk download failed', error);
            }
            return;
        }

        const isDownloadV2Call = checkDownloadV2Call(spendDetails?.spendInformation, tableData?.totalRecords);
        props.downloadSheet({
            dataModelId: spendDetails.dataModelId,
            appid: props.match.params.appid,
            fields,
            downloadType: 'excel',
            conditions: [
                {
                    selectedConditionClause: 'and',
                    selectedConditionDataElement: 'documentId',
                    selectedConditionOperator: 'equals',
                    value: props.blockState['documentId'],
                },
                ...currentConditions,
            ],
            dataUniqueId: props.data.chartUniqueId + '_' + 'all_spend_download',
            isVisible: true,
            key: 'xlsx',
            name: 'Excel',
            _id: '',
            isDownloadV2Call: isDownloadV2Call,
        });
    };

    const handleSortChange = (newSort) => {
        setSort(newSort);
        setPage(0); // Reset to first page when sort changes
        const currentConditions = getConditions();
        props.getSpendDetails({
            appId: props.match.params.appid,
            dataModelId: spendDetails.dataModelId,
            mainDataModelId: spendDetails.mainDataModelId,
            documentId: props.match.params.id,
            dataUniqueId: props.data?.chartUniqueId + '_spendDetails_tableData',
            conditions: currentConditions,
            targetField: spendDetails?.targetField || 'documentId',
            field: spendDetails?.field || 'documentId',
            sort: newSort,
            payload: {
                dataModelId: spendDetails.dataModelId,
                mainDataModelId: spendDetails.mainDataModelId,
                documentId: props.match.params.id,
                conditions: currentConditions,
                // targetField: spendDetails?.targetField || 'documentId',
                // field: spendDetails?.field || 'documentId',
                ...(spendDetails?.targetField && { targetField: spendDetails?.targetField || 'documentId' }),
                ...(spendDetails?.field && { field: spendDetails?.field || 'documentId' }),
                ...(spendDetails?.sumField && { sumField: spendDetails?.sumField }),
                ...(spendDetails?.combineFieldsIntoSingleField && { combineFieldsIntoSingleField: true }),
                sort: newSort,
                page: 0,
                rowsPerPage: rowsPerPage
            }
        });
    };

    /** Opens Add New Spend form modal. */
    const handleOpenAddSpendModal = () => {
        const actionButton = spendDetails?.spendInformationHeader?.actionButton;
        if (!actionButton?.enable) {
            return;
        }
        const initialFormData = {};
        const targetField = spendDetails?.targetField || actionButton?.updateField || 'contract_id';
        initialFormData[targetField] = resolveTemplateValue('${blockState.documentId}');
        setAddSpendFormData(initialFormData);
        setIsAddSpendModalOpen(true);
    };

    /** Updates a field value in the Add Spend form. */
    const handleAddSpendFieldChange = (name, value) => {
        setAddSpendFormData((previous) => ({
            ...previous,
            [name]: value,
        }));
    };

    /** Creates a spend row via create API, then refreshes the table. */
    const handleSaveAddSpend = async () => {
        const actionButton = spendDetails?.spendInformationHeader?.actionButton;
        const dataModelId = actionButton?.dataModelId || spendDetails?.dataModelId;
        if (!dataModelId) {
            toast.error('Spend data model is not configured');
            return;
        }

        setIsAddSpendSaving(true);
        try {
            await FormService.createSpendDataModel({
                appId: props.match.params.appid,
                dataModelId,
                payload: { ...addSpendFormData },
            });
            toast.success('Spend added successfully');
            setIsAddSpendModalOpen(false);
            setAddSpendFormData({});
            refetchSpendTable();
        } catch (error) {
            toast.error(error?.data?.message || error?.message || 'Unable to add spend');
        } finally {
            setIsAddSpendSaving(false);
        }
    };

    /** Opens Link New Spend modal and loads candidate rows. */
    const handleOpenLinkSpendModal = () => {
        const updateButton = spendDetails?.spendInformationHeader?.updateButton;
        if (!updateButton?.enable) {
            return;
        }
        setLinkModalSelectedIds([]);
        setLinkModalTableData({ data: [] });
        setLinkModalPage(0);
        setLinkModalRowsPerPage(10);
        setLinkModalConditions([]);
        setLinkModalFilterData({});
        setIsLinkSpendModalOpen(true);
        fetchLinkModalSpendDetails({
            requestedPage: 0,
            requestedRowsPerPage: 10,
            requestedConditions: [],
        });

        const linkFilterFields = updateButton?.filter?.map(item => item.value).filter(Boolean) || [];
        if (linkFilterFields.length > 0) {
            props.getSpendFilters({
                appId: props.match.params.appid,
                dataModelId: updateButton?.dataModelId || spendDetails.dataModelId,
                dataUniqueId: linkModalFilterDataUniqueId,
                payload: {
                    field: linkFilterFields.join(','),
                      documentIdField: spendDetails?.linkColumn || 'documentId',
                    ...(spendDetails?.linkColumn && { linkColumn: spendDetails?.linkColumn || 'documentId' }),
                    ...(spendDetails?.targetField && { targetField: spendDetails?.targetField || 'documentId' }),
                    mainDataModelId: spendDetails.mainDataModelId,
                },
            });
        }
    };

    /** Fetches another candidate page while preserving modal selections. */
    const handleLinkModalPageChange = (event, newPage) => {
        setLinkModalPage(newPage);
        fetchLinkModalSpendDetails({
            requestedPage: newPage,
            requestedRowsPerPage: linkModalRowsPerPage,
            requestedConditions: linkModalConditions,
        });
    };

    /** Changes candidate page size and returns to the first page. */
    const handleLinkModalRowsPerPageChange = (event) => {
        const nextRowsPerPage = parseInt(event.target.value, 10);
        setLinkModalRowsPerPage(nextRowsPerPage);
        setLinkModalPage(0);
        fetchLinkModalSpendDetails({
            requestedPage: 0,
            requestedRowsPerPage: nextRowsPerPage,
            requestedConditions: linkModalConditions,
        });
    };

    /** Converts ExternalFilter values to spend API conditions for the link modal. */
    const handleLinkModalFilters = (filterValues) => {
        const filterConfig = spendDetails?.spendInformationHeader?.updateButton?.filter || [];
        const nextConditions = [];

        filterConfig.forEach((item) => {
            const filterValue = filterValues?.[item.value];
            if (filterValue && Array.isArray(filterValue) && filterValue.length > 0) {
                nextConditions.push({
                    selectedConditionClause: 'and',
                    selectedConditionDataElement: item.value,
                    selectedConditionOperator: 'in',
                    value: filterValue.map(entry => (entry && typeof entry === 'object' && 'value' in entry ? entry.value : entry)),
                });
                return;
            }

            if (filterValue && typeof filterValue === 'object' && (filterValue.startDate || filterValue.endDate)) {
                nextConditions.push({
                    selectedConditionClause: 'and',
                    selectedConditionDataElement: item.value,
                    selectedConditionOperator: 'between',
                    value: [filterValue.startDate, filterValue.endDate].filter(Boolean),
                });
            }
        });

        setLinkModalConditions(nextConditions);
        setLinkModalPage(0);
        fetchLinkModalSpendDetails({
            requestedPage: 0,
            requestedRowsPerPage: linkModalRowsPerPage,
            requestedConditions: nextConditions,
        });
    };

    /** Links selected modal rows to the current obligation document. */
    const handleSaveLinkSpend = async () => {
        const updateButton = spendDetails?.spendInformationHeader?.updateButton;
        const dataModelId = updateButton?.dataModelId || spendDetails?.dataModelId;
        const whereKey = updateButton?.where || 'id_spendByContract';
        const updateField = updateButton?.updateField || 'contract_id';
        const updateValue = resolveTemplateValue(updateButton?.updateValue || '${blockState.documentId}');

        if (!dataModelId) {
            toast.error('Spend data model is not configured');
            return;
        }
        if (!linkModalSelectedIds.length) {
            toast.error('Please select at least one spend record');
            return;
        }

        setIsLinkModalSaving(true);
        try {
            await FormService.updateSpendDataModel({
                appId: props.match.params.appid,
                dataModelId,
                payload: {
                    where: {
                        key: whereKey,
                        value: linkModalSelectedIds.length === 1 ? linkModalSelectedIds[0] : linkModalSelectedIds,
                    },
                    update: [
                        { key: updateField, value: updateValue },
                    ],
                },
            });
            toast.success('Spend linked successfully');
            setIsLinkSpendModalOpen(false);
            setLinkModalSelectedIds([]);
            refetchSpendTable();
        } catch (error) {
            toast.error(error?.data?.message || error?.message || 'Unable to link spend');
        } finally {
            setIsLinkModalSaving(false);
        }
    };

    /** Unlinks one or more spend rows by clearing the configured update field. */
    const unlinkSpendRecords = async (whereValues) => {
        const unLinkButton = spendDetails?.spendInformationHeader?.unLinkButton;
        const dataModelId = unLinkButton?.dataModelId || spendDetails?.dataModelId;
        const whereKey = unLinkButton?.where || 'id_spendByContract';
        const updateField = unLinkButton?.updateField || 'contract_id';
        const values = Array.isArray(whereValues) ? whereValues.filter((value) => value != null && value !== '') : [whereValues];

        if (!dataModelId) {
            toast.error('Spend data model is not configured');
            return;
        }
        if (!values.length) {
            toast.error('Please select at least one spend record');
            return;
        }

        setIsUnlinkSaving(true);
        try {
            await FormService.linkSpendDataModel({
                appId: props.match.params.appid,
                dataModelId,
                payload: {
                    where: {
                        key: whereKey,
                        value: values.length === 1 ? values[0] : values,
                    },
                    update: [
                        { key: updateField, value: null },
                    ],
                },
            });
            toast.success('Spend unlinked successfully');
            setSelectedDocumentIds([]);
            refetchSpendTable();
        } catch (error) {
            toast.error(error?.data?.message || error?.message || 'Unable to unlink spend');
        } finally {
            setIsUnlinkSaving(false);
        }
    };

    /** Unlinks a single row from the actions column. */
    const handleRowUnlink = (event, row) => {
        if (event?.stopPropagation) {
            event.stopPropagation();
        }
        const whereValue = row?.[unlinkWhereKey] ?? row?.id_spendByContract ?? row?.id ?? row?._id;
        unlinkSpendRecords(whereValue);
    };

    /** Unlinks all currently selected checkbox rows. */
    const handleUnlinkSelected = () => {
        unlinkSpendRecords(selectedDocumentIds);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {
                spendDetails?.overview?.enable && (
                    <OverviewCardViewer {...props} src={spendDetails?.overview?.src} title={spendDetails?.overview?.title} description={spendDetails?.overview?.description} />
                )
            }
            {
                spendDetails?.uploadCard?.enable && (
                    <SpendCardViewer
                        {...props}
                        data={spendDetails?.uploadCard?.dataElement}
                        blockState={cardData}
                        isLoading={props.formData?.[props.data?.chartUniqueId + '_spendDetails_tableData_loading'] || props.formData?.[props.data?.chartUniqueId + '_spendDetails_filterData_loading']}
                    />
                )
            }
            {
                spendDetails?.spendInformationHeader?.enable && (
                    <HeadingBarViewer
                        {...props}
                        title={spendDetails?.spendInformationHeader?.title}
                        buttonLabel={spendDetails?.spendInformationHeader?.actionButton?.enable
                            ? spendDetails?.spendInformationHeader?.actionButton?.label
                            : ''}
                        onButtonClick={handleOpenAddSpendModal}
                        isAttachmentsButton={false}
                        blockState={props.blockState || {}}
                        enableWorkflow={false}
                        data={{}}
                        updateButtonLabel={spendDetails?.spendInformationHeader?.updateButton?.enable
                            ? spendDetails?.spendInformationHeader?.updateButton?.label
                            : ''}
                        onUpdateButtonClick={handleOpenLinkSpendModal}
                    />
                )
            }
            {
                (spendDetails?.spendInformation?.downloadSelected || spendDetails?.spendInformation?.downloadAll || spendDetails?.spendInformationHeader?.unLinkButton?.enable || (spendDetails?.spendInformation?.filter && spendDetails?.spendInformation?.filter?.length > 0)) && (
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'end', alignItems: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'end', alignItems: 'center', gap: '10px', marginRight: '12px' }}>
                            {spendDetails?.spendInformation?.filter && spendDetails?.spendInformation?.filter?.length > 0 && (
                                <Button
                                    variant="contained"
                                    endIcon={<ArrowDropDownIcon />}
                                    onClick={handleFilterClick}
                                    style={{
                                        backgroundColor: '#1976d2',
                                        color: 'white',
                                        textTransform: 'none'
                                    }}
                                >
                                    {filterButtonText}
                                </Button>
                            )}
                        </div>
                        <div className={classes.buttonsContainer}>
                            {spendDetails?.spendInformation?.downloadSelected && <Button
                                className={classes.downloadButton}
                                startIcon={<GetAppIcon style={{ fontSize: '16px' }} />}
                                onClick={handleDownload}
                                disabled={selectedDocumentIds.length === 0}
                            >
                                Download Selected
                            </Button>
                            }
                            {spendDetails?.spendInformationHeader?.unLinkButton?.enable && (
                                <Button
                                    className={classes.downloadButton}
                                    onClick={handleUnlinkSelected}
                                    disabled={selectedDocumentIds.length === 0 || isUnlinkSaving}
                                >
                                    {spendDetails?.spendInformationHeader?.unLinkButton?.label || 'Unlink Spend'}
                                </Button>
                            )}
                            {spendDetails?.spendInformation?.downloadAll && <Button
                                className={classes.downloadButton}
                                startIcon={<GetAppIcon style={{ fontSize: '16px' }} />}
                                onClick={handleDownloadAll}
                                disabled={selectedDocumentIds.length > 0}
                            >
                                Download All
                            </Button>}
                        </div>
                    </div>
                )
            }
            {
                spendDetails?.spendInformation?.enable && (
                    (props.formData?.[props.data?.chartUniqueId + '_spendDetails_tableData_loading'] || props.formData?.[props.data?.chartUniqueId + '_spendDetails_filterData_loading'] || props.formData?.[props.data?.chartUniqueId + '_all_spend_download_loading'] || props.formData?.[props.data?.chartUniqueId + '_single_row_download_loading'] || isUnlinkSaving)
                        ? (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px 0' }}>
                                <CircularProgress className={classes.loader} size={28} />
                            </div>
                        )
                        : (
                            <SimpleTableViewer
                                {...props}
                                title={spendDetails?.spendInformation?.title}
                                dataObj={tableData?.data}
                                selectedDataElement={[]}
                                dataModel="66e8297ca40acb5c5c38248a"
                                selectedTableDataElement={spendDetails?.spendInformation?.dataElement}
                                isActionEnabled={spendDetails?.spendInformation?.actions?.enable}
                                isObjectTable={spendDetails?.spendInformation?.actions?.isObjectTable}
                                pagination={true}
                                page={page}
                                rowsPerPage={rowsPerPage}
                                onPageChange={handlePageChange}
                                onRowsPerPageChange={handleRowsPerPageChange}
                                totalRecords={tableData?.totalRecords}
                                onDownload={handleSingleRowDownload}
                                isDownloadButton={spendDetails?.spendInformation?.actions?.isDownload}
                                isCheckbox={isCheckboxEnabled}
                                sort={sort}
                                onSortChange={handleSortChange}
                                onCheckboxChange={(updatedSelection) => setSelectedDocumentIds(updatedSelection)}
                                selectedRows={selectedDocumentIds}
                                rowIdKey={unlinkWhereKey}
                                isLinkButton={spendDetails?.spendInformation?.actions?.isLinkButton}
                                linkButtonTitle="Unlink"
                                onLink={handleRowUnlink}
                                isActionColumnSticky={true}
                            />
                        )
                )
            }
            <AddSpendModal
                {...props}
                open={isAddSpendModalOpen}
                onClose={() => {
                    if (!isAddSpendSaving) {
                        setIsAddSpendModalOpen(false);
                    }
                }}
                onSave={handleSaveAddSpend}
                title={spendDetails?.spendInformationHeader?.actionButton?.label || 'Add New Spend'}
                formDataElements={spendDetails?.spendInformationHeader?.actionButton?.dataElement || []}
                formData={addSpendFormData}
                handleOnFieldChange={handleAddSpendFieldChange}
                isSaving={isAddSpendSaving}
            />
            <LinkSpendModal
                {...props}
                open={isLinkSpendModalOpen}
                onClose={() => {
                    if (!isLinkModalSaving) {
                        setIsLinkSpendModalOpen(false);
                        setLinkModalSelectedIds([]);
                    }
                }}
                onSave={handleSaveLinkSpend}
                title={spendDetails?.spendInformationHeader?.updateButton?.label || 'Link New Spend'}
                tableData={linkModalTableData}
                selectedTableDataElement={spendDetails?.spendInformationHeader?.updateButton?.dataElement || spendDetails?.spendInformation?.dataElement || []}
                selectedRows={linkModalSelectedIds}
                onCheckboxChange={(updatedSelection) => setLinkModalSelectedIds(updatedSelection)}
                rowIdKey={linkWhereKey}
                isLoading={Boolean(props.formData?.[`${linkModalDataUniqueId}_loading`])}
                isSaving={isLinkModalSaving}
                isCheckboxEnabled={spendDetails?.spendInformationHeader?.updateButton?.isEnableCheckbox?.enable !== false}
                isEnableMultipleSelection={spendDetails?.spendInformationHeader?.updateButton?.isEnableCheckbox?.isEnableMultipleSelection !== false}
                isEnableSelectAll={spendDetails?.spendInformationHeader?.updateButton?.isEnableCheckbox?.isEnableSelectAll === true}
                isSearchEnabled={spendDetails?.spendInformationHeader?.updateButton?.isEnableSearch?.enable === true}
                filterConfig={spendDetails?.spendInformationHeader?.updateButton?.filter || []}
                filterData={linkModalFilterData}
                page={linkModalPage}
                rowsPerPage={linkModalRowsPerPage}
                onPageChange={handleLinkModalPageChange}
                onRowsPerPageChange={handleLinkModalRowsPerPageChange}
                onApplyFilters={handleLinkModalFilters}
                getSpendFilters={props.getSpendFilters}
                dataModelId={spendDetails?.spendInformationHeader?.updateButton?.dataModelId || spendDetails.dataModelId}
                appId={props.match.params.appid}
                filterDataUniqueId={linkModalFilterDataUniqueId}
                formData={props.formData}
            />
            {/* Filter Popper */}
            <Popper
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                style={{ zIndex: 2000 }}
            >
                <ClickAwayListener onClickAway={() => handleFilterClose('close')}>
                    <Paper
                        className={classes.filterPopper}
                    >
                        <Box style={{ padding: '20px' }}>
                            <Box style={{ marginBottom: '20px' }}>
                                <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: '16px' }}>
                                    Filter
                                </Typography>
                                <ExternalFilter
                                    {...props}
                                    externalFilters={spendDetails?.spendInformation?.filter?.map(item => ({
                                        value: item.value,
                                        label: item.label,
                                        type: item.isDate ? 'date' : 'select'
                                    })) || []}
                                    externalFilterData={spendDetails?.spendInformation?.filter?.map((item, index) =>
                                        // (filterData?.[item.value] || []).map(option => ({ label: option, value: option }))
                                        (filterData?.[item.value] || []).map(option => {
                                            const isNumeric = typeof option === 'number' || (option != null && option !== '' && !isNaN(Number(option)));
                                            const numVal = typeof option === 'number' ? option : Number(option);

                                            return {
                                                label: isNumeric ? numVal : (option ?? ''),
                                                value: isNumeric ? numVal : String(option ?? '')
                                            };
                                        })
                                    )}
                                    externalFilterValues={localSelectedFilterData !== null ? localSelectedFilterData : {}}
                                    onExternalFilterChange={handleExternalFilterChange}
                                    setLocalSelectedFilterData={setLocalSelectedFilterData}
                                    localSelectedFilterData={localSelectedFilterData}
                                    align="vertical"
                                    isNewV1Theme={true}
                                    dataModelId={spendDetails.dataModelId}
                                    appId={props.match.params.appid}
                                    dataUniqueId={props.data?.chartUniqueId + '_spendDetails_filterData'}
                                    getModelDataDropDown={(payload) => {
                                        // Map to getSpendFilters format
                                        props.getSpendFilters({
                                            appId: payload.appId,
                                            dataModelId: payload.id,
                                            dataUniqueId: payload.dataUniqueId,
                                            payload: {
                                                field: payload.field,
                                                obligationId: props.match.params.id,
                                                documentIdField: spendDetails?.linkColumn || 'documentId',
                                                ...(spendDetails?.linkColumn && { linkColumn: spendDetails?.linkColumn || 'documentId' }),
                                                ...(spendDetails?.targetField && { targetField: spendDetails?.targetField || 'documentId' }),
                                                mainDataModelId: spendDetails.mainDataModelId
                                            }
                                        });
                                    }}
                                    formData={props.formData}
                                    conditions={getConditions()}
                                />
                            </Box>
                            <Box style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                paddingTop: '16px',
                                borderTop: '1px solid #e0e0e0'
                            }}>
                                {spendDetails?.spendInformation?.filter && spendDetails?.spendInformation?.filter?.length > 0 && (
                                    <Button
                                        onClick={() => {
                                            const clearedFilters = {};
                                            spendDetails?.spendInformation?.filter?.forEach((item) => {
                                                clearedFilters[item.value] = null;
                                            });
                                            setLocalSelectedFilterData(clearedFilters);
                                            handleFilterClose('clear');
                                        }}
                                        // disabled={!hasLocalFilterValues()}
                                        style={{
                                            color: '#1976d2',
                                            textTransform: 'capitalize'
                                        }}
                                    >
                                        Clear All
                                    </Button>
                                )}
                                <div style={{ display: 'flex', gap: '10px', marginLeft: 'auto' }}>
                                    <Button
                                        onClick={() => handleFilterClose('close')}
                                        style={{
                                            color: '#1976d2',
                                            textTransform: 'capitalize',
                                            height: 32,
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        Close
                                    </Button>
                                    <Button
                                        onClick={() => handleFilterClose('save')}
                                        // disabled={!hasLocalFilterValues()}
                                        variant="contained"
                                        style={{
                                            backgroundColor: '#1976d2',
                                            color: 'white',
                                            textTransform: 'capitalize',
                                            height: 32,
                                            boxSizing: 'border-box'
                                        }}
                                    >
                                        Save
                                    </Button>
                                </div>
                            </Box>
                        </Box>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </div>
    )
};

export default SpendViewer;